import React from 'react';
import { Box, createFilterOptions, debounce } from '@mui/material';
import { history } from 'app';
import { SelectOption } from 'new/components/Form/Select';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { useSearchMembers } from 'new/modules/MembersPage/hooks';
import { DEBOUNCE_TYPING_TIMEOUT } from 'new/utils/constants';
import findRoute from 'new/routing/routes';
import { Form } from 'new/components/Form';
import { useGetLocationGroups } from 'new/modules/ManageGroups/hooks';
import { useGetLocations } from 'new/modules/ManageLocations/hooks';
import { useSidebarStore } from 'new/hooks/useSidebarStore';
import { useGetUsersList } from 'new/modules/ManageUsers/hooks';

const PopperComponent: React.FC = ({ children }) => (
  <Box display="flex" flexDirection="column">
    {children}
  </Box>
);

const searchRouteMap = {
  member: findRoute('MembersPage'),
  location: findRoute('ManageLocations'),
  group: findRoute('ManageGroups'),
  user: findRoute('ManageUsers'),
};

type EnhancedSelectOption = SelectOption & {
  type: 'member' | 'location' | 'group' | 'user';
};

const SearchMembers: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { activeOfficeId, user, isSuperAdmin,orgId } = useActiveOffice();
  const {
    data: members,
    isLoading,
    mutate: searchMembers,
    reset,
  } = useSearchMembers(activeOfficeId);
  const { data: groups = [] } = useGetLocationGroups(Number(orgId));
  const { data: locations = [] } = useGetLocations(
    Number(orgId),
    user?.data.userId,
  );
  const { data: users = [] } = useGetUsersList(Number(orgId));
  const { viewAsStandard } = useSidebarStore();
  const isSuperAdminView = isSuperAdmin && !viewAsStandard;

  const handleInputChange = debounce((searchQuery = '') => {
    if (searchQuery?.length > 1) {
      searchMembers({ searchQuery });
    } else {
      reset();
    }
  }, DEBOUNCE_TYPING_TIMEOUT);

  const handleChange = (option: EnhancedSelectOption | null) => {
    if (option) {
      const route = searchRouteMap[option.type].getPath?.(option.value);

      history.push(route);
      onClose();
    }
  };

  const filterOptions = createFilterOptions<SelectOption>({ limit: 5 });

  const superAdminOptions = [
    ...groups.map((group) => ({
      label: `[G] ${group.name}`,
      value: group.id,
      type: 'group',
    })),
    ...locations.map((location) => ({
      label: location.locationName,
      value: location.id,
      type: 'location',
    })),
    ...users.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
      type: 'user',
    })),
    ...users.map((user) => ({
      label: user.email,
      value: user.id,
      type: 'user',
    })),
    ...(members?.data.map((item) => ({
      label: `${item.firstName} ${item.lastName}`,
      value: item.id,
      type: 'member',
    })) || [])
  ];

  const options = isSuperAdminView
    ? superAdminOptions
    : members?.data.map((item) => ({
        label: `${item.firstName} ${item.lastName}`,
        value: item.id,
        type: 'member',
      })) || [];

  const label = isSuperAdminView ? 'Search' : 'Find a member';

  return (
    <Box width={300} p={2}>
      <Form initialValues={{ search: '' }}>
        <Form.Autocomplete
          name="search"
          label={label}
          filterOptions={filterOptions}
          options={options}
          onChange={(option) => handleChange(option as EnhancedSelectOption)}
          onInputChange={handleInputChange}
          isLoading={isLoading}
          withSearchHighlight
          PopperComponent={PopperComponent}
          PaperComponent={Box}
          noOptionsText=""
          fullWidth
          autoFocus
        />
      </Form>
    </Box>
  );
};

export default SearchMembers;
